import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { GalleryDetail, GallerySummary, ImageAsset, SiteSummary } from '../../models/cms-models';
import { GalleriesService } from '../../services/galleries-service';
import { ImagesService } from '../../services/images-service';
import { SiteContextService } from '../../services/site-context.service';

@Component({
  selector: 'app-galleries-page',
  imports: [CommonModule, FormsModule],
  templateUrl: './galleries-page.html',
  styleUrl: './galleries-page.css',
})
export class GalleriesPage implements OnInit {
  private siteContext = inject(SiteContextService);
  private galleriesService = inject(GalleriesService);
  private imagesService = inject(ImagesService);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  availableImages: ImageAsset[] = [];
  galleries: GallerySummary[] = [];
  selectedSiteId: string | null = null;
  selectedGalleryId: string | null = null;
  draftName = '';
  draftImageIds: string[] = [];
  imageToAdd = '';
  loading = false;
  saving = false;
  deleting = false;
  error: string | null = null;
  private loadedSiteId: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    combineLatest([this.siteContext.sites$, this.siteContext.activeSiteId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([sites, activeSiteId]) => {
        void this.syncSelectedSite(sites, activeSiteId);
      });
  }

  get selectedSiteName(): string {
    return this.sites.find((site) => site.id === this.selectedSiteId)?.name ?? 'site';
  }

  get draftImages(): ImageAsset[] {
    const imagesById = new Map(this.availableImages.map((image) => [image.id, image]));
    return this.draftImageIds
      .map((imageId) => imagesById.get(imageId))
      .filter((image): image is ImageAsset => !!image);
  }

  get unselectedImages(): ImageAsset[] {
    const selectedImageIds = new Set(this.draftImageIds);
    return this.availableImages.filter((image) => !selectedImageIds.has(image.id));
  }

  startNewGallery(): void {
    this.selectedGalleryId = null;
    this.draftName = '';
    this.draftImageIds = [];
    this.imageToAdd = this.unselectedImages[0]?.id ?? '';
  }

  async selectGallery(galleryId: string): Promise<void> {
    this.selectedGalleryId = galleryId;
    this.loading = true;
    this.error = null;

    try {
      const gallery = await this.galleriesService.getGallery(galleryId);
      this.applyGallery(gallery);
    } catch {
      this.error = 'Failed to load the selected gallery.';
    } finally {
      this.loading = false;
    }
  }

  addImage(): void {
    if (!this.imageToAdd || this.draftImageIds.includes(this.imageToAdd)) {
      return;
    }

    this.draftImageIds = [...this.draftImageIds, this.imageToAdd];
    this.imageToAdd = this.unselectedImages[0]?.id ?? '';
  }

  removeImage(imageId: string): void {
    this.draftImageIds = this.draftImageIds.filter((existing) => existing !== imageId);
    this.imageToAdd = this.unselectedImages[0]?.id ?? '';
  }

  moveImage(imageId: string, direction: -1 | 1): void {
    const index = this.draftImageIds.indexOf(imageId);
    const targetIndex = index + direction;

    if (index < 0 || targetIndex < 0 || targetIndex >= this.draftImageIds.length) {
      return;
    }

    const reordered = [...this.draftImageIds];
    const [selectedImageId] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, selectedImageId);
    this.draftImageIds = reordered;
  }

  async saveGallery(): Promise<void> {
    if (!this.selectedSiteId || !this.draftName.trim()) {
      return;
    }

    this.saving = true;
    this.error = null;

    const payload = {
      siteId: this.selectedSiteId,
      name: this.draftName.trim(),
      imageIds: [...this.draftImageIds],
    };

    try {
      const saved = this.selectedGalleryId
        ? await this.galleriesService.updateGallery(this.selectedGalleryId, payload)
        : await this.galleriesService.createGallery(payload);

      await this.reloadSiteWorkspace(this.selectedSiteId);
      this.applyGallery(saved);
    } catch {
      this.error = 'Failed to save the gallery.';
    } finally {
      this.saving = false;
    }
  }

  async deleteGallery(): Promise<void> {
    if (!this.selectedGalleryId || this.deleting) {
      return;
    }

    const confirmed = window.confirm(`Delete "${this.draftName}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    this.deleting = true;
    this.error = null;

    try {
      await this.galleriesService.deleteGallery(this.selectedGalleryId);
      if (this.selectedSiteId) {
        await this.reloadSiteWorkspace(this.selectedSiteId);
      }
      this.startNewGallery();
    } catch {
      this.error = 'Failed to delete the gallery. It may still be in use.';
    } finally {
      this.deleting = false;
    }
  }

  private async syncSelectedSite(sites: SiteSummary[], activeSiteId: string | null): Promise<void> {
    this.sites = sites;

    if (!sites.length) {
      this.selectedSiteId = null;
      this.availableImages = [];
      this.galleries = [];
      this.loadedSiteId = null;
      this.startNewGallery();
      return;
    }

    const targetSiteId = this.siteContext.ensureValidActiveSite(activeSiteId);
    if (!targetSiteId) {
      return;
    }

    this.selectedSiteId = targetSiteId;

    if (this.loadedSiteId === targetSiteId) {
      return;
    }

    await this.reloadSiteWorkspace(targetSiteId);
    this.startNewGallery();
  }

  private async reloadSiteWorkspace(siteId: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const [images, galleries] = await Promise.all([
        this.imagesService.getSiteImages(siteId),
        this.galleriesService.getSiteGalleries(siteId),
      ]);
      this.availableImages = images;
      this.galleries = galleries;
      this.loadedSiteId = siteId;
      this.imageToAdd = this.unselectedImages[0]?.id ?? '';
    } catch {
      this.availableImages = [];
      this.galleries = [];
      this.error = 'Failed to load galleries for the selected site.';
    } finally {
      this.loading = false;
    }
  }

  private applyGallery(gallery: GalleryDetail): void {
    this.selectedGalleryId = gallery.id;
    this.draftName = gallery.name;
    this.draftImageIds = gallery.images.map((image) => image.id);
    this.imageToAdd = this.unselectedImages[0]?.id ?? '';
  }
}
